/**
 * ToolsTab — Tool search & category browser for the sidebar toolbar.
 *
 * Uses useToolCatalog() for tool data (not ToolRegistry directly).
 */

import { Clock } from 'lucide-react';
import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { useToolCatalog } from '@/hooks/useToolCatalog';
import type { ITool } from '@/interfaces/ITool';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';

import { CategoryGroup } from './CategoryGroup';
import { ToolCard } from './ToolCard';
import { resolveCategory } from './toolCategories';

const SectionHeader: React.FC<{
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, icon, action }) => (
  <div className="flex items-center justify-between px-2 py-1">
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {icon}
      {title}
    </div>
    {action}
  </div>
);

export interface ToolsTabProps {
  onSelectTool: (toolId: string) => void;
  onAddToPipeline: (toolId: string) => void;
}

export const ToolsTab: React.FC<ToolsTabProps> = ({ onSelectTool, onAddToPipeline }) => {
  const query = useToolBrowserStore((s) => s.query);
  const favorites = useToolBrowserStore((s) => s.favorites);
  const toggleFavorite = useToolBrowserStore((s) => s.toggleFavorite);
  const recentTools = useToolBrowserStore((s) => s.recentTools);
  const clearRecents = useToolBrowserStore((s) => s.clearRecents);
  const viewMode = useToolBrowserStore((s) => s.viewMode);
  const closeDrawer = useLayoutStore((s) => s.closeDrawer);
  const pipelineAddMode = useLayoutStore((s) => s.pipelineAddMode);

  const { getAllTools } = useToolCatalog();
  const allTools = useMemo(() => getAllTools(), [getAllTools]);

  const filteredTools = useMemo(() => {
    if (!query.trim()) return allTools;
    const q = query.toLowerCase();
    return allTools.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.category ?? '').toLowerCase().includes(q) ||
        (t.keywords ?? []).some((kw) => kw.toLowerCase().includes(q)),
    );
  }, [allTools, query]);

  const recentToolObjects = useMemo(() => {
    const toolMap = new Map(allTools.map((t) => [t.id, t]));
    return recentTools
      .map((r) => toolMap.get(r.toolId))
      .filter((t): t is ITool => t !== undefined)
      .slice(0, 5);
  }, [allTools, recentTools]);

  const groupedTools = useMemo(() => {
    const groups = new Map<string, { meta: ReturnType<typeof resolveCategory>; tools: ITool[] }>();
    for (const tool of filteredTools) {
      const meta = resolveCategory(tool);
      const existing = groups.get(meta.label);
      if (existing) {
        existing.tools.push(tool);
      } else {
        groups.set(meta.label, { meta, tools: [tool] });
      }
    }
    return new Map([...groups.entries()].sort(([a], [b]) => a.localeCompare(b)));
  }, [filteredTools]);

  const sortedFlat = useMemo(
    () => [...filteredTools].sort((a, b) => a.name.localeCompare(b.name)),
    [filteredTools],
  );

  const isFavorite = React.useCallback(
    (toolId: string) => favorites.includes(toolId),
    [favorites],
  );
  const isSearching = query.trim().length > 0;

  const handleSelectTool = React.useCallback((toolId: string) => {
    if (pipelineAddMode) {
      onAddToPipeline(toolId);
    } else {
      onSelectTool(toolId);
    }
    closeDrawer();
  }, [onSelectTool, onAddToPipeline, pipelineAddMode, closeDrawer]);

  const handleAddToPipeline = React.useCallback((toolId: string) => {
    onAddToPipeline(toolId);
    closeDrawer();
  }, [onAddToPipeline, closeDrawer]);

  const renderCard = (tool: ITool, grid = false) => (
    <ToolCard
      key={tool.id}
      tool={tool}
      isFavorite={isFavorite(tool.id)}
      onToggleFavorite={toggleFavorite}
      onSelect={handleSelectTool}
      onAddToPipeline={handleAddToPipeline}
      grid={grid}
    />
  );

  return (
    <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-3">
      {isSearching && (
        <div className="px-2 text-xs text-muted-foreground">
          {filteredTools.length} result{filteredTools.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
        </div>
      )}

      {!isSearching && recentToolObjects.length > 0 && (
        <section>
          <SectionHeader
            title="Recent"
            icon={<Clock className="h-3 w-3" />}
            action={
              <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-muted-foreground" onClick={clearRecents}>
                Clear
              </Button>
            }
          />
          <div className="space-y-0.5">
            {recentToolObjects.map((tool) => renderCard(tool))}
          </div>
        </section>
      )}

      <section>
        {!isSearching && <SectionHeader title="All Tools" />}

        {viewMode === 'categorized' && (
          <div className="space-y-0.5">
            {[...groupedTools.entries()].map(([label, { meta, tools }]) => (
              <CategoryGroup key={label} categoryName={label} icon={meta.icon} toolCount={tools.length}>
                {tools.map((tool) => renderCard(tool))}
              </CategoryGroup>
            ))}
          </div>
        )}

        {viewMode === 'flat' && (
          <div className="space-y-0.5">{sortedFlat.map((tool) => renderCard(tool))}</div>
        )}

        {viewMode === 'grid' && (
          <div className="grid grid-cols-2 gap-1.5">{sortedFlat.map((tool) => renderCard(tool, true))}</div>
        )}

        {filteredTools.length === 0 && (
          <div className="px-2 py-8 text-center text-sm text-muted-foreground">No tools found.</div>
        )}
      </section>
    </div>
  );
};